/* eslint-disable no-unused-vars, react/sort-comp */
import { StateStore } from '@bingads-webui/state-store';
import type { Subscription } from '@bingads-webui-universal/observable';
import React from 'react';
import _ from 'underscore';

type StoreFactory<S extends StateStore> = S | (() => S);
type StoreSelector<S extends StateStore> = string | ((store: S) => object);
type StoreState<S extends StateStore> = S['state'];

export function withStateStore<T extends object, S extends StateStore>(
  ComponentToWrap: React.ComponentType<T>,
  storeFactory: StoreFactory<S>,
  selector: StoreSelector<S> = 'store'
) {
  if (typeof storeFactory !== 'function') {
    const store = storeFactory;
    // eslint-disable-next-line no-param-reassign
    storeFactory = () => store;
  }

  let select: (store: S) => object;
  if (typeof selector === 'string') {
    const storeName: string = selector;
    select = (store: S) => {
      const res: Record<string, S> = {};
      res[storeName] = store;
      return res;
    };
  } else {
    select = selector;
  }

  interface RefProps {
    forwardedRef: React.ForwardedRef<unknown>;
  }

  class WrappedComponent extends React.PureComponent<Partial<T> & RefProps> {
    private subscription: Subscription<StoreState<S>> | undefined;

    private store: S;

    constructor(props: Partial<T> & RefProps) {
      super(props);
      this.state = {};
      this.store = this.createStore();
    }

    componentDidMount() {
      this.subscribe();
    }

    componentWillUnmount() {
      this.unsubscribe();
    }

    createStore(): S {
      if (storeFactory instanceof StateStore) {
        return storeFactory;
      }

      return storeFactory();
    }

    subscribe() {
      this.subscription = this.store.subscribe((state: StoreState<S>) => {
        if (!this.subscription?.closed) {
          this.setState(state);
        }
      });
    }

    unsubscribe() {
      this.subscription!.unsubscribe();
    }

    render() {
      const props = _.extend({}, this.props, select(this.store));

      return <ComponentToWrap {...props} ref={this.props.forwardedRef} />;
    }
  }
  return React.forwardRef((props: Partial<T>, ref: React.ForwardedRef<unknown>) => (
    <WrappedComponent {...props} forwardedRef={ref} />
  ));
}
